import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** FAQ page accordions: skip scroll-into-view so expanding a question does not jump the page. */
  get #isFaqQuestionAccordion() {
    return this.hasAttribute('data-faq-question');
  }

  #controller = new AbortController();
  #isInitialized = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #toggleInProgress = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    // Close sibling accordions when one opens
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Initialize ResizeObserver for scroll handling during animations
    this.#initResizeObserver();

    // Set initialized flag after a short delay to prevent scroll on page load
    setTimeout(() => {
      this.#isInitialized = true;
    }, 100);
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    // Disconnect the ResizeObserver
    this.#resizeObserver?.disconnect();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Stop event from bubbling up to parent elements (prevents closing menu drawer)
    event.stopPropagation();

    // If accordion is closed and will open, do scroll first
    if (!this.details.open && this.#isInitialized) {
      event.preventDefault();
      
      const isMobile = isMobileBreakpoint();
      const isDesktop = !isMobile;

      // Don't proceed if accordion behavior is disabled
      if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
        return;
      }

      // FAQ questions: no scroll-to-first-item (that jumped the page) and no artificial delay
      if (this.#isFaqQuestionAccordion) {
        this.#closeSiblingAccordions();
        this.details.open = true;
        return;
      }

      // Step 1: Scroll to first accordion in group
      this.#scrollIntoViewWithOffset();

      // Step 2: Close sibling accordions after scroll starts
      setTimeout(() => {
        this.#closeSiblingAccordions();
      }, 100);

      // Step 3: Open this accordion after siblings are closed
      setTimeout(() => {
        this.details.open = true;
      }, 300);
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const hasOpenByDefaultMobile = this.hasAttribute('open-by-default-on-mobile');
    const hasOpenByDefaultDesktop = this.hasAttribute('open-by-default-on-desktop');
    const isMobile = isMobileBreakpoint();

    // If both attributes exist, always keep open (used in header mega menu context)
    if (hasOpenByDefaultMobile && hasOpenByDefaultDesktop) {
      this.details.open = true;
      return;
    }

    const shouldBeOpen =
      (isMobile && hasOpenByDefaultMobile) ||
      (!isMobile && hasOpenByDefaultDesktop);

    this.details.open = shouldBeOpen;
  }

  /**
   * Initializes the ResizeObserver to handle scroll during CSS animations
   */
  // #initResizeObserver() {
  //   this.#resizeObserver = new ResizeObserver(() => {
  //     // Only scroll if accordion is open, initialized, and toggle is in progress
  //     if (this.details.open && this.#isInitialized && this.#toggleInProgress) {
  //       this.#scrollIntoViewWithOffset();
  //     }
  //   });

  //   // Observe the details element for size changes during animations
  //   this.#resizeObserver.observe(this.details);
  // }

  #initResizeObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.details.open || !this.#isInitialized || !this.#toggleInProgress) {
        return;
      }

      if (this.#isFaqQuestionAccordion) {
        return;
      }

      // defer side effects
      requestAnimationFrame(() => {
        this.#scrollIntoViewWithOffset();
      });
    });

    // Observe the details element for size changes during animations
    this.#resizeObserver.observe(this.details);
  }

  /**
   * Closes sibling accordions in the same group
   */
  #closeSiblingAccordions() {
    // Find the closest accordion group container
    const accordionGroup =
      this.closest('[data-accordion-group]') ||
      this.closest('faq-section') ||
      this.closest('.block') ||
      this.parentElement;

    if (!accordionGroup) return;

    /** @type {NodeListOf<HTMLElement>} */
    const siblingAccordions = accordionGroup.querySelectorAll('accordion-custom');

    siblingAccordions.forEach((accordion) => {
      // Skip if it's the current accordion
      if (accordion === this) return;

      // Skip if this accordion is a descendant of the current accordion (nested)
      if (this.contains(accordion)) return;

      // Skip if not a child of the same group
      if (!accordionGroup.contains(accordion)) return;

      const details = accordion.querySelector('details');
      if (details && details.open) {
        details.removeAttribute('open');
      }
    });
  }

  #scrollIntoViewWithOffset() {
    const offset = isMobileBreakpoint() ? 130 : 150;

    // Find the accordion group
    const accordionGroup =
      this.closest('[data-accordion-group]') ||
      this.closest('faq-section') ||
      this.closest('.block') ||
      this.parentElement;

    // Find the first accordion in the group
    const firstAccordion = accordionGroup?.querySelector('accordion-custom');
    const targetElement = firstAccordion || this;

    const top =
      targetElement.getBoundingClientRect().top +
      window.pageYOffset -
      offset;

    if (accordionGroup?.closest('.menu-drawer__menu')) return;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  }

  /**
   * Ensures only one accordion is open at a time within the same accordion group
   * Looks for closest parent container with data-accordion-group attribute
   * If not found, scopes to the parent block element
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    if (!this.#isInitialized) return;

    // Mark that toggle is in progress for ResizeObserver
    this.#toggleInProgress = true;

    // Reset the flag after animation completes (typically 300-500ms)
    setTimeout(() => {
      this.#toggleInProgress = false;
    }, 500);
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
